import PrimaryLoadingButton from "@components/PrimaryLoadingButton";
import { useProductDetailContext } from "@providers/ProductDetailProvider";
import { PrimaryButton, css, styled } from "@sourceful/shared-components";
import { ProductMain, ProductType } from "@sourceful/shared-types";
import toRem from "@styles/postprocess/conversion/to-rem";
import { useRouter } from "next/router";
import React from "react";
import { Link } from "react-scroll";
import { routeToLeadGenUrl } from "../productOverview/helpers";

export const PRODUCT_DETAIL_STICKY_BAR_HEIGHT = 90;

const Wrapper = styled("div", {
  position: "sticky",
  top: "-5px",
  display: "flex",

  width: "100%",
  minWidth: "100%",
  maxWidth: "100%",
  alignItems: "center",
  overflow: "overlay",
  backgroundColor: "rgba(246, 246, 246, 0.85)",
  backdropFilter: "blur(12px)",
  zIndex: 10,
  variants: {
    isPreview: {
      true: {
        top: 29,
      },
    },
  },
});

const StyledNav = styled("nav", {
  width: "100%",
  maxWidth: "1200px",
  minWidth: "300px",
  margin: "0 auto",
  height: toRem(PRODUCT_DETAIL_STICKY_BAR_HEIGHT),
  display: "flex",
  justifyContent: "space-between",
  paddingTop: toRem(20),
  paddingBottom: toRem(20),

  ".active-link": {
    textDecoration: "underline",
    borderRadius: "$pill",
    fontWeight: "bold",
  },

  "a:not([data-cy='start-quote-button'])": {
    marginLeft: toRem(10),
    userSelect: "none",
    fontFamily: "Space Grotesk",
    fontSize: "$p2",
    lineHeight: 1.6,
    textDecoration: "none",
    paddingInlineStart: "10px",
    paddingInlineEnd: "10px",
    height: "50px",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#DCDCDD",
      borderRadius: "$pill",
    },
  },
});

const CtaButtons = styled("div", {
  marginLeft: toRem(20),
  display: "flex",
  alignItems: "center",
  "& > button": {
    "& + button": {
      marginLeft: toRem(20),
    },
  },
});

const buttonOverrideStyles = css({ flexShrink: 0, height: "fit-content", alignSelf: "center" });

export interface ProductDetailStickyBarProps {
  product: ProductMain;
  renderConfigureButton: boolean;
  useRFQCta?: boolean; // an override for live priced product PPC landing pages
}

const ProductDetailStickyBar: React.FC<ProductDetailStickyBarProps> = ({
  product,
  renderConfigureButton,
  useRFQCta,
}) => {
  const router = useRouter();
  const isRFQ = product.type === ProductType.RFQ;
  const { selectedPreset, onRequestQuoteClicked } = useProductDetailContext();
  const { productDetailPageContent } = product;

  const renderPrimaryCTA = () => {
    if (useRFQCta || isRFQ) {
      return (
        <PrimaryButton
          compact={true}
          borderRadius={"square"}
          data-cy="start-quote-button"
          as="a"
          href={routeToLeadGenUrl(router, product.slug)}
        >
          Request quote
        </PrimaryButton>
      );
    }

    const ctaProps = {
      dataCy: "request-quote-button",
      isLoading: false,
      label: "Request quote",
      handleClick: () => onRequestQuoteClicked({ preset: selectedPreset }),
    };

    if (renderConfigureButton) {
      return (
        <PrimaryLoadingButton
          data-cy={ctaProps.dataCy}
          isLoading={ctaProps.isLoading}
          handleClick={ctaProps.handleClick}
          defaultLabel={ctaProps.label}
          loadingMessage="Loading"
          className={buttonOverrideStyles()}
        />
      );
    }
  };

  return (
    <Wrapper isPreview={router?.isPreview}>
      <StyledNav aria-label={"In page navigation"}>
        {productDetailPageContent?.map(section => {
          return (
            // @ts-ignore
            <Link
              key={section._key}
              to={`s${section._key}`}
              smooth
              duration={500}
              activeClass="active-link"
              spy
              offset={-PRODUCT_DETAIL_STICKY_BAR_HEIGHT - 200}
              href={`#s${section._key}`}
            >
              {section.menuLabel || section.title}
            </Link>
          );
        })}

        <CtaButtons>{renderPrimaryCTA()}</CtaButtons>
      </StyledNav>
    </Wrapper>
  );
};

export default ProductDetailStickyBar;
